import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Layers, Search, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  KIND_LABELS,
  getAllEvaluationRecords,
  getKindVisual,
  type KindLabel,
} from "../mocks/evaluation-records";

export default function InnovationRecordsHome() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<"全部" | KindLabel>("全部");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const counts = useMemo(() => {
    const all = getAllEvaluationRecords();
    const map = new Map<string, number>();
    map.set("全部", all.length);
    for (const k of KIND_LABELS) map.set(k, 0);
    for (const r of all) {
      map.set(r.kind, (map.get(r.kind) ?? 0) + 1);
    }
    return map;
  }, []);

  const filtered = useMemo(() => {
    const all = getAllEvaluationRecords();
    const q = query.trim().toLowerCase();
    return all.filter((r) => {
      if (category !== "全部" && r.kind !== category) return false;
      if (!q) return true;
      return r.title.toLowerCase().includes(q);
    });
  }, [query, category]);

  const menuRows = useMemo(() => {
    const rows: { key: "全部" | KindLabel; label: string; Icon: LucideIcon }[] = [
      { key: "全部", label: "全部", Icon: Layers },
      ...KIND_LABELS.map((k) => {
        const v = getKindVisual(k);
        return { key: k, label: k, Icon: v.Icon };
      }),
    ];
    return rows;
  }, []);

  return (
    <div className="min-h-full pb-10">
      <div className="mb-5 rounded-2xl border border-border bg-card p-4 shadow-sm md:p-5">
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索评价记录..."
            className="h-11 rounded-xl border-border bg-background pl-10 text-sm shadow-sm"
          />
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-11 w-full items-center justify-between gap-3 rounded-xl border border-border bg-background px-3 text-left text-sm shadow-sm transition-colors hover:bg-accent/40"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium text-foreground">{category}</span>
            </span>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", menuOpen && "rotate-180")} />
          </button>

          {menuOpen && (
            <div className="absolute left-0 right-0 z-50 mt-2 max-h-[min(22rem,calc(100vh-12rem))] overflow-auto rounded-xl border border-border bg-popover py-1 shadow-lg">
              {menuRows.map((row) => {
                const selected = category === row.key;
                const cnt = counts.get(row.key === "全部" ? "全部" : row.key) ?? 0;
                const RowIcon = row.Icon;
                return (
                  <button
                    key={row.key}
                    type="button"
                    onClick={() => {
                      setCategory(row.key);
                      setMenuOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition-colors",
                      selected ? "bg-primary/10 text-foreground" : "hover:bg-accent/60",
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <RowIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{row.label}</span>
                    </span>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                        selected ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {cnt}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((r) => {
          const { Icon, wrap, icolor } = getKindVisual(r.kind);
          const scoreMuted = r.status === "优化中";
          return (
            <Link
              key={r.id}
              to={`/innovation/records/${r.id}`}
              className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm transition-all hover:border-primary/20 hover:shadow-md md:gap-4 md:p-5"
            >
              <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl md:h-12 md:w-12", wrap)}>
                <Icon className={cn("h-5 w-5 md:h-[22px] md:w-[22px]", icolor)} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold leading-snug text-foreground md:text-base">{r.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {r.kind} · {r.date}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 pl-2">
                <span
                  className={cn(
                    "text-xl font-bold tabular-nums md:text-2xl",
                    scoreMuted ? "text-muted-foreground" : "text-primary",
                  )}
                >
                  {r.score}
                </span>
                <span className={cn("text-xs font-medium", r.status === "优化中" ? "text-muted-foreground" : "text-foreground")}>
                  {r.status}
                </span>
              </div>
              <ChevronRight className="hidden h-5 w-5 shrink-0 text-muted-foreground/50 sm:block" />
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="flex min-h-[200px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
          没有找到匹配的记录，试试其它关键词或分类。
        </div>
      )}
    </div>
  );
}
